import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/config";
import { readCache, writeCache, formatSyncedAgo } from "../lib/adminCache";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, Users, Calendar, MessageSquare, Star, CreditCard, ListChecks, Bell, Mail, Smartphone, RefreshCw, CheckCircle2, Sun, UserPlus, Heart, Link as LinkIcon, Send, Settings as SettingsIcon, CalendarPlus, FileText, XCircle, Eye, HandCoins, Pencil, MapPin, Archive, Trash2, DollarSign, Search, ClipboardList, WifiOff } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import NotificationCenter from "../components/NotificationCenter";
import AutoPayEnrollmentControl from "../components/AutoPayEnrollmentControl";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import JobActionsMenu, { PaymentBadge, StatusBadge, GetDirectionsButton, LastReminderChip } from "../components/JobActionsMenu";
import PaymentMethodChip from "../components/PaymentMethodChip";
import ChangePaymentMethodDialog from "../components/ChangePaymentMethodDialog";
import UpdatePriceDialog from "../components/UpdatePriceDialog";
import IncompleteBookingsPanel from "../components/IncompleteBookingsPanel";
import PendingPaymentsPanel from "../components/PendingPaymentsPanel";
import PaymentMethodReports from "../components/PaymentMethodReports";
import BookingSqftVerifyButton from "../components/BookingSqftVerifyButton";
import ConfidenceChip from "../components/ConfidenceChip";
import PropertyMapDialog from "../components/PropertyMapDialog";
import RetentionStatsCard from "../components/RetentionStatsCard";
import ReviewsTipsDashboardCard from "../components/ReviewsTipsDashboardCard";
import CalendarMonthView from "../components/CalendarMonthView";
import RouteMapPanel from "../components/RouteMapPanel";
import NearbyOpportunitiesCard from "../components/NearbyOpportunitiesCard";
import AbandonedSignups from "../components/AbandonedSignups";
import EditCustomerDialog from "../components/EditCustomerDialog";
import AdminPropertiesPanel from "../components/AdminPropertiesPanel";
import CampaignsPanel from "../components/CampaignsPanel";
import DashboardSummary from "../components/DashboardSummary";
import LeadsPanel from "../components/LeadsPanel";
import ThumbtackWebhookPanel from "../components/ThumbtackWebhookPanel";
import SourceBadge, { LEAD_SOURCES, LEAD_SOURCE_META } from "../components/SourceBadge";
import SendQuoteDialog from "../components/SendQuoteDialog";
import RecurringFrequencyFields from "../components/RecurringFrequencyFields";
import InvitationStatusBadge from "../components/InvitationStatusBadge";
import InvitationProgressTracker from "../components/InvitationProgressTracker";
import FeedbackStatusBadge from "../components/FeedbackStatusBadge";
import { toast } from "sonner";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
// iter247 (2026-08-22) — Shared "pending booking" definition, used by
// BOTH the Admin Dashboard BOOKINGS card and the Admin Bookings tab so
// they never diverge.  A booking is "pending" (i.e. still active work
// on the schedule board) unless it has ONE of the terminal statuses
// below.  This intentionally covers `scheduled`, `pending`,
// `confirmed`, and any other transient status the backend introduces
// without needing a code change.
const TERMINAL_BOOKING_STATUSES = new Set([
  "cancelled", "canceled", "completed", "rejected", "deleted",
  // Defensive extras seen in the wild
  "refunded", "voided", "expired", "no_show",
  "archived", "removed",
]);
// Handle every messy input:
//  * null / undefined booking
//  * `status` is null / missing / whitespace  →  count as pending
//  * `status` uppercased or padded            →  normalised
//  * booking soft-deleted via `deleted_at`    →  NOT pending
export const isPendingBooking = (b) => {
  if (!b || typeof b !== "object") return false;
  if (b.deleted_at || b.is_deleted) return false;
  const raw = (b.status == null ? "" : String(b.status)).trim().toLowerCase();
  return !TERMINAL_BOOKING_STATUSES.has(raw);
};
const PAYMENT_STATUSES = ["unpaid", "autopay_scheduled", "payment_processing", "pay_on_arrival", "pending_verification", "payment_pending", "paid", "refunded"];
const RECURRING_FREQS = new Set(["weekly", "biweekly", "monthly"]);
const FREQ_OPTIONS = [
  { key: "one_time", label: "One-Time" },
  { key: "weekly", label: "Weekly" },
  { key: "biweekly", label: "Every 2 weeks" },
  { key: "monthly", label: "Monthly" },
];
// iter216 (2026-03-01) — Cash / Cash on Arrival removed as a selectable
// option per owner directive. Stripe Checkout is the default. Historical
// records still render via label maps elsewhere.
const PAYMENT_OPTIONS = [
  { key: "stripe", label: "Credit/Debit Card (Stripe)" },
  { key: "applepay", label: "Apple Pay" },
  { key: "googlepay", label: "Google Pay" },
  { key: "zelle", label: "Zelle" },
  { key: "cashapp", label: "Cash App" },
  { key: "venmo", label: "Venmo" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "bookings";

  const [bookings, setBookings] = useState([]);
  const [todayJobs, setTodayJobs] = useState([]);
  const [todayDate, setTodayDate] = useState("");
  const [customers, setCustomers] = useState([]);
  const [pmDialogCust, setPmDialogCust] = useState(null);   // customer object or null
  const [customerStatusFilter, setCustomerStatusFilter] = useState("active"); // active|recurring|one_time|cancelled|archived
  const [customerSearch, setCustomerSearch] = useState("");
  const [pricingCustomer, setPricingCustomer] = useState(null);   // for recurring-price dialog
  const [hideCancelledJobs, setHideCancelledJobs] = useState(true);
  const [bookingSourceFilter, setBookingSourceFilter] = useState("");
  const [editCust, setEditCust] = useState(null);
  const [restorePrompt, setRestorePrompt] = useState(null); // iter59: {user_id, name, email, phone, archived_at}
  const [estimates, setEstimates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notifStatus, setNotifStatus] = useState({ smtp_configured: false, sms_configured: false });
  // iter246 (2026-08-22) — Admin Panel is now offline-first: the layout
  // renders immediately even before any /admin/* call resolves. Each
  // section is hydrated from IndexedDB cache (if present) then refreshed
  // in the background. Customers + Bookings load FIRST so a customer
  // phone call is always fast; secondary sections come next.
  // Retained for backwards-compat with callers that reference `loadingData`
  // (e.g. an admin tour or feature-flag). The render no longer gates on it.
  const [loadingData] = useState(false);
  // Sync status pill state — { online, savedAt, refreshing }
  const [syncMeta, setSyncMeta] = useState({
    online: (typeof navigator !== "undefined") ? navigator.onLine : true,
    savedAt: null,
    refreshing: false,
  });
  // Force the "last synced" copy to refresh once a minute
  const [, setSyncTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSyncTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const lastCustomerFilterRef = useRef(null);
  const [completing, setCompleting] = useState(null);
  const [checkingIn, setCheckingIn] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [startingRoute, setStartingRoute] = useState(false);
  const [tips, setTips] = useState([]);
  const [tipsTotal, setTipsTotal] = useState(0);
  const [settings, setSettings] = useState({ google_review_url: "", review_delay_hours: 24, require_payment_before_scheduling: false });
  const [savingSettings, setSavingSettings] = useState(false);
  const [services, setServices] = useState([]);
  // Add Customer dialog
  const [addCustOpen, setAddCustOpen] = useState(false);
  const [newCust, setNewCust] = useState({
    name: "", email: "", phone: "", address: "", notes: "",
    service_frequency: "one_time", custom_days: null,
    preferred_service_day: "any", preferred_time_window: "morning",
    start_date: "", service_key: "lawn_mowing", square_feet: 2000, yard_area: "both",
    auto_send_invitation: true,
  });
  // Conversion stats
  const [convStats, setConvStats] = useState({ invitations_sent: 0, activated: 0, activation_rate: 0, first_time: 0, recurring: 0 });
  // Schedule for Customer dialog
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedCustomer, setSchedCustomer] = useState(null);
  const [schedForm, setSchedForm] = useState({
    service_key: "lawn_mowing", square_feet: 2000, frequency: "one_time", yard_area: "both",
    preferred_date: "", preferred_time: "9:00 AM", payment_method: "stripe", notes: "",
  });
  const [schedSubmitting, setSchedSubmitting] = useState(false);
  // iter42 — Admin-only Property Map dialog
  const [mapBooking, setMapBooking] = useState(null);
  // Send Quote dialog
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteCustomer, setQuoteCustomer] = useState(null);
  // Quotes list
  const [quotes, setQuotes] = useState([]);
  const [quotesCounts, setQuotesCounts] = useState({});
  const [activeQuotes, setActiveQuotes] = useState(0);
  // Cancel Job dialog
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  // Offers
  const [offers, setOffers] = useState([]);
  const [offersCounts, setOffersCounts] = useState({});
  const [activeOffers, setActiveOffers] = useState(0);
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterOffer, setCounterOffer] = useState(null);
  const [counterPrice, setCounterPrice] = useState("");
  const [counterMessage, setCounterMessage] = useState("");
  const [counterAction, setCounterAction] = useState(null); // accept | counter | reject | null

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  // iter246 — Per-section loader. Each call:
  //   * hydrates local state from IndexedDB cache (if any) INSTANTLY
  //   * fires a network refresh with a 12s timeout
  //   * on success — writes the value back to cache + updates state
  //   * on failure — leaves cached state untouched (never blanks the UI)
  // Every caller passes a cache key and an `apply(fresh)` function that
  // splashes the value into React state. The `network()` returns the
  // NORMALISED value to persist (e.g. `data.bookings || []`).
  const _refreshSection = async (key, network, apply) => {
    try {
      const cached = await readCache(key);
      // iter247c — Only hydrate from cache if the cached value has real
      // content.  An empty [] left over from a previous session's
      // failure would otherwise beat the network refresh to the state
      // setter and (worse) look identical to "loaded but zero results".
      const cachedIsUseful =
        cached && cached.value != null &&
        !(Array.isArray(cached.value) && cached.value.length === 0);
      if (cachedIsUseful) {
        try { apply(cached.value); } catch { /* noop */ }
        setSyncMeta((m) => ({
          ...m,
          savedAt: Math.max(m.savedAt || 0, cached.savedAt || 0),
        }));
      }
    } catch { /* IndexedDB may be unavailable — fall through to network */ }
    try {
      const value = await Promise.race([
        network(),
        new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 12000)),
      ]);
      try { apply(value); } catch { /* noop */ }
      writeCache(key, value); // fire-and-forget
      setSyncMeta((m) => ({ ...m, online: true, savedAt: Date.now() }));
      return value;
    } catch (err) {
      // Distinguish offline vs slow: if navigator.onLine is false, mark
      // offline; otherwise leave the previous sync state alone.
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        setSyncMeta((m) => ({ ...m, online: false }));
      }
      // Never blank the UI — cached state above still holds.
      return null;
    }
  };

  // Priority-ordered admin loader. Customers + bookings are P0 and fire
  // FIRST, then everything else in parallel.  We do NOT `await` these —
  // each section shows its cached data immediately.
  const loadAll = async () => {
    setSyncMeta((m) => ({ ...m, refreshing: true }));
    const custKey = `admin:customers:${customerStatusFilter || "active"}`;
    lastCustomerFilterRef.current = customerStatusFilter || "active";

    // P0 — Customers + Bookings (independent, in parallel, both cached)
    const p0 = [
      _refreshSection(custKey,
        () => api.get("/admin/customers", { params: { status: customerStatusFilter }, timeout: 12000 })
                  .then((r) => r.data.customers || []),
        (v) => setCustomers(v)),
      _refreshSection("admin:bookings",
        () => api.get("/admin/bookings", { timeout: 12000 })
                  .then((r) => r.data.bookings || []),
        (v) => setBookings(v)),
      _refreshSection("admin:today-jobs",
        () => api.get("/admin/today-jobs", { timeout: 12000 })
                  .then((r) => ({ bookings: r.data.bookings || [], date: r.data.date || "" })),
        (v) => { setTodayJobs(v.bookings || []); setTodayDate(v.date || ""); }),
    ];

    // P1 — Quotes + Offers (needed for many admin flows)
    const p1 = [
      _refreshSection("admin:quotes",
        () => api.get("/admin/quotes", { timeout: 12000 })
                  .then((r) => ({ quotes: r.data.quotes || [], counts: r.data.counts || {}, active: r.data.active || 0 })),
        (v) => { setQuotes(v.quotes); setQuotesCounts(v.counts); setActiveQuotes(v.active); }),
      _refreshSection("admin:offers",
        () => api.get("/admin/offers", { timeout: 12000 })
                  .then((r) => ({ offers: r.data.offers || [], counts: r.data.counts || {}, active: r.data.active || 0 })),
        (v) => { setOffers(v.offers); setOffersCounts(v.counts); setActiveOffers(v.active); }),
      _refreshSection("admin:services",
        () => api.get("/services?include_quote_only=true", { timeout: 12000 })
                  .then((r) => r.data.services || []),
        (v) => setServices(v)),
    ];

    // P2 — Everything else (estimates, messages, reviews, payments, notifications, tips, settings, conv stats)
    const p2 = [
      _refreshSection("admin:estimates",
        () => api.get("/admin/estimates", { timeout: 12000 }).then((r) => r.data.estimates || []),
        (v) => setEstimates(v)),
      _refreshSection("admin:messages",
        () => api.get("/admin/messages", { timeout: 12000 }).then((r) => r.data.messages || []),
        (v) => setMessages(v)),
      _refreshSection("admin:reviews",
        () => api.get("/admin/reviews", { timeout: 12000 }).then((r) => r.data.reviews || []),
        (v) => setReviews(v)),
      _refreshSection("admin:payments",
        () => api.get("/admin/payments", { timeout: 12000 }).then((r) => r.data.payments || []),
        (v) => setPayments(v)),
      _refreshSection("admin:notifications",
        () => api.get("/admin/notifications", { timeout: 12000 })
                  .then((r) => ({
                    notifications: r.data.notifications || [],
                    smtp_configured: !!r.data.smtp_configured,
                    sms_configured: !!r.data.sms_configured,
                  })),
        (v) => {
          setNotifications(v.notifications);
          setNotifStatus({ smtp_configured: v.smtp_configured, sms_configured: v.sms_configured });
        }),
      _refreshSection("admin:tips",
        () => api.get("/admin/tips", { timeout: 12000 })
                  .then((r) => ({ tips: r.data.tips || [], total_paid: r.data.total_paid || 0 })),
        (v) => { setTips(v.tips); setTipsTotal(v.total_paid); }),
      _refreshSection("admin:settings",
        () => api.get("/admin/settings", { timeout: 12000 }).then((r) => ({
          google_review_url: r.data.google_review_url || "",
          review_delay_hours: r.data.review_delay_hours || 24,
          require_payment_before_scheduling: !!r.data.require_payment_before_scheduling,
        })),
        (v) => setSettings(v)),
      _refreshSection("admin:conv-stats",
        () => api.get("/admin/conversion-stats", { timeout: 12000 }).then((r) => r.data || {}),
        (v) => setConvStats(v || {})),
    ];

    // Fire everything without blocking the render. P0 first — awaited so
    // "refreshing" flips off only when the most-critical calls resolve.
    Promise.allSettled(p0).finally(() => {
      Promise.allSettled([...p1, ...p2]).finally(() =>
        setSyncMeta((m) => ({ ...m, refreshing: false }))
      );
    });
  };

  useEffect(() => { if (user?.is_admin) loadAll(); }, [user, customerStatusFilter]);

  // iter246 — Auto-reconnect: when the browser flips back online, refresh
  // Customers + Bookings automatically so cached data catches up without
  // requiring a page reload.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => {
      setSyncMeta((m) => ({ ...m, online: true }));
      if (user?.is_admin) loadAll();
    };
    const onOffline = () => setSyncMeta((m) => ({ ...m, online: false }));
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Instant client-side customer search — fast filter on the cached list so
  // typing is snappy even with hundreds of customers.
  const filteredCustomers = (() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      const hay = [c.name, c.email, c.phone, c.default_address, c.formatted_address]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  })();

  // iter246 — Never gate the whole Admin Panel on a spinner. The
  // auth-loading state is a real block (we don't know if you're an admin
  // yet), but once we do, render immediately and let each section
  // hydrate from cache while background fetches resolve.
  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-auth-spinner"><Loader2 className="w-7 h-7 animate-spin text-[#047857]" /></div>;
  }
  if (!user?.is_admin) return null;

  // Small header pill shown once we have data or a known offline state.
  const syncPillText = (() => {
    if (!syncMeta.online) {
      return syncMeta.savedAt
        ? `Offline · synced ${formatSyncedAgo(syncMeta.savedAt)}`
        : "Offline · no cache yet";
    }
    if (syncMeta.refreshing && !syncMeta.savedAt) return "Loading customers…";
    if (syncMeta.refreshing) return `Refreshing · ${formatSyncedAgo(syncMeta.savedAt)}`;
    if (syncMeta.savedAt) return `Live · synced ${formatSyncedAgo(syncMeta.savedAt)}`;
    return "Loading…";
  })();

  const updateBooking = async (id, patch) => {
    try {
      await api.patch(`/admin/bookings/${id}`, patch);
      toast.success("Booking updated");
      loadAll();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleReview = async (id, approved) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { approved });
      toast.success("Updated");
      loadAll();
    } catch {
      toast.error("Failed");
    }
  };

  const resendNotification = async (id) => {
    try {
      const { data } = await api.post(`/admin/notifications/${id}/resend`);
      if (data.ok) toast.success("Notification resent");
      else toast.error(data.info || "Could not resend");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  const markComplete = async (b) => {
    if (completing) return;
    const recurringNote = RECURRING_FREQS.has(b.frequency)
      ? "\n\nNext visit will be created (unpaid). Card on file will be charged for THIS visit only — no prepayment for future visits."
      : "\n\nCard on file will be charged now for this visit.";
    if (!window.confirm(`Mark ${b.service_name} for ${b.name} as completed?${recurringNote}`)) return;
    setCompleting(b.booking_id);
    try {
      const { data } = await api.post(`/admin/bookings/${b.booking_id}/complete`);
      const nextLine = data.next_booking ? ` Next visit: ${data.next_booking.preferred_date}.` : "";
      if (data.charge_ok) {
        toast.success(`Completed + charged $${(data.invoice?.amount || 0).toFixed(0)}.${nextLine}`);
      } else if (data.charge_info === "no_saved_card") {
        toast.success(`Completed. Invoice generated — collect at site.${nextLine}`);
      } else if (data.charge_info === "cash_on_arrival") {
        toast.success(`Completed. Cash collection due.${nextLine}`);
      } else if ((data.charge_info || "").startsWith("manual:")) {
        toast.success(`Completed. Manual payment pending.${nextLine}`);
      } else if (data.charge_info === "already_paid") {
        toast.success(`Completed. Already paid.${nextLine}`);
      } else {
        toast.error(`Completed but auto-charge failed: ${data.charge_info}. Booking flagged Payment Due.`);
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark complete failed");
    } finally {
      setCompleting(null);
    }
  };

  const retryPayment = async (b) => {
    if (!window.confirm(`Retry charging $${(b.invoice_amount || b.price || 0).toFixed(2)} to ${b.name}'s saved card?`)) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.booking_id}/retry-payment`, {});
      if (data.ok) {
        toast.success("Payment captured");
      } else {
        toast.error(`Retry failed: ${data.charge_info}`);
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  // iter95: Send a payment reminder from the admin booking card. Handles
  // both flows:
  //   - Completed but not-paid (auto-charge failed OR non-Stripe method):
  //     hits /admin/bookings/{id}/remind-to-pay (has 30-min cooldown).
  //   - Not-yet-completed pending Stripe checkout: hits
  //     /admin/pending-payments/{id}/send-reminder (creates/reuses session).
  const remindToPayInline = async (b) => {
    const isCompleted = (b.status || "").toLowerCase() === "completed";
    const url = isCompleted
      ? `/admin/bookings/${b.booking_id}/remind-to-pay`
      : `/admin/pending-payments/${b.booking_id}/send-reminder`;
    try {
      const { data } = await api.post(url, {});
      const sent = (data?.sent || data?.reminder?.delivery || {});
      const channels = Object.entries(sent)
        .filter(([, v]) => v === true || (v && v.status === "sent"))
        .map(([k]) => k.toUpperCase())
        .join(" · ");
      toast.success(channels ? `Reminder sent — ${channels}` : "Reminder queued");
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail?.message || "Could not send reminder") : (detail || "Could not send reminder");
      toast.error(msg);
    }
  };

  // iter82: Admin-side removal of a customer's saved Stripe card. Also
  // disables auto-charge and detaches the PaymentMethod on Stripe.
  const removeCustomerSavedCard = async (b) => {
    if (!b?.user_id) {
      toast.error("This booking is not linked to a customer account");
      return;
    }
    const who = b.name || b.email || "this customer";
    if (!window.confirm(`Remove ${who}'s saved card? Automatic billing will be disabled until they book again.`)) return;
    try {
      const { data } = await api.delete(`/admin/customers/${b.user_id}/saved-card`);
      if (data?.removed) {
        toast.success("Saved card removed");
      } else {
        toast.info("No saved card was on file");
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove card");
    }
  };

  const captureGeo = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  });

  const checkIn = async (b) => {
    if (checkingIn) return;
    setCheckingIn(b.booking_id);
    try {
      const geo = await captureGeo();
      await api.post(`/admin/bookings/${b.booking_id}/checkin`, geo || { lat: null, lng: null });
      toast.success("Checked in. Customer notified.");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check-in failed");
    } finally { setCheckingIn(null); }
  };

  const checkOut = async (b) => {
    if (checkingOut) return;
    if (!window.confirm(`Check out and mark ${b.service_name} for ${b.name} complete?`)) return;
    setCheckingOut(b.booking_id);
    try {
      const geo = await captureGeo();
      await api.post(`/admin/bookings/${b.booking_id}/checkout`, geo || { lat: null, lng: null });
      toast.success("Checked out. Customer notified · service complete.");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check-out failed");
    } finally { setCheckingOut(null); }
  };

  const startRoute = async () => {
    if (startingRoute) return;
    setStartingRoute(true);
    try {
      const { data } = await api.post("/admin/route/start", {});
      toast.success(`Route alert sent — ${data.jobs} jobs queued today`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start route");
    } finally { setStartingRoute(false); }
  };

  const submitNewCustomer = async () => {
    if (!newCust.name.trim()) return toast.error("Name is required");
    try {
      const { data } = await api.post("/admin/customers/manual", {
        name: newCust.name,
        email: newCust.email || null,
        phone: newCust.phone || null,
        address: newCust.address || null,
        notes: newCust.notes || null,
        service_frequency: newCust.service_frequency || "one_time",
        custom_days: newCust.custom_days,
        preferred_service_day: newCust.preferred_service_day,
        preferred_time_window: newCust.preferred_time_window,
        start_date: newCust.start_date || null,
        service_key: newCust.service_key,
        square_feet: newCust.square_feet,
        yard_area: newCust.yard_area,
        auto_send_invitation: newCust.auto_send_invitation,
      });
      const parts = ["Customer added"];
      if (data?.recurring_generated) parts.push(`${data.recurring_generated} visits scheduled`);
      if (data?.invitation?.sent) parts.push("invitation emailed");
      else if (data?.invitation && newCust.email && newCust.auto_send_invitation) parts.push("invite email failed — copy link manually");
      toast.success(parts.join(" · "));
      setAddCustOpen(false);
      setNewCust({
        name: "", email: "", phone: "", address: "", notes: "",
        service_frequency: "one_time", custom_days: null,
        preferred_service_day: "any", preferred_time_window: "morning",
        start_date: "", service_key: "lawn_mowing", square_feet: 2000, yard_area: "both",
        auto_send_invitation: true,
      });
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Backend now returns a rich object for iter59 duplicate cases. Fall
      // back to string handling for legacy messages.
      if (detail && typeof detail === "object") {
        console.error("Add customer failed", detail, e?.response);
        if (detail.archived_match) {
          setRestorePrompt({ ...detail.archived_match, __kind: "archived" });
          toast.error(detail.message || "Archived customer matches — choose Restore or Create New.");
          return;
        }
        if (detail.deleted_match) {
          setRestorePrompt({ ...detail.deleted_match, __kind: "deleted" });
          toast.error(detail.message || "This email belongs to a deleted customer.");
          return;
        }
        toast.error(detail.message || `Add customer failed: ${detail.code || "unknown"}`);
        return;
      }
      console.error("Add customer failed", e?.response?.status, e?.response?.data, e);
      toast.error(
        (typeof detail === "string" && detail)
        || e?.response?.data?.message
        || (e?.response?.status ? `Add customer failed (HTTP ${e.response.status})` : "Could not add customer — network error"),
      );
    }
  };

  const restoreArchivedCustomer = async () => {
    if (!restorePrompt?.user_id) return;
    const kind = restorePrompt.__kind || "archived";
    const endpoint = kind === "deleted" ? "restore" : "unarchive";
    try {
      await api.post(`/admin/customers/${restorePrompt.user_id}/${endpoint}`);
      toast.success(`${restorePrompt.name || "Customer"} restored`);
      setRestorePrompt(null);
      setAddCustOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore customer");
    }
  };

  const deleteForeverFromRestorePrompt = async () => {
    if (!restorePrompt?.user_id) return;
    if (!window.confirm("Delete this customer FOREVER? Their email/phone will be released for reuse.")) return;
    try {
      await api.post(`/admin/customers/${restorePrompt.user_id}/delete-forever`);
      toast.success("Deleted forever — email + phone released");
      setRestorePrompt(null);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete forever");
    }
  };

  const createNewDespiteArchived = async () => {
    // Retry with force_new_after_archived/force_new_after_deleted so the
    // backend sentinel-izes the previous record and creates a fresh customer.
    const kind = restorePrompt?.__kind || "archived";
    setRestorePrompt(null);
    try {
      const { data } = await api.post("/admin/customers/manual", {
        name: newCust.name,
        email: newCust.email || null,
        phone: newCust.phone || null,
        address: newCust.address || null,
        notes: newCust.notes || null,
        service_frequency: newCust.service_frequency || "one_time",
        custom_days: newCust.custom_days,
        preferred_service_day: newCust.preferred_service_day,
        preferred_time_window: newCust.preferred_time_window,
        start_date: newCust.start_date || null,
        service_key: newCust.service_key,
        square_feet: newCust.square_feet,
        yard_area: newCust.yard_area,
        auto_send_invitation: newCust.auto_send_invitation,
        force_new_after_archived: kind === "archived",
        force_new_after_deleted: kind === "deleted",
      });
      const parts = ["New customer created"];
      if (data?.recurring_generated) parts.push(`${data.recurring_generated} visits scheduled`);
      toast.success(parts.join(" · "));
      setAddCustOpen(false);
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" ? detail.message : detail) || "Could not create new customer";
      console.error("Create new despite archived failed", e?.response);
      toast.error(msg);
    }
  };

  const sendInvite = async (c) => {
    try {
      const { data } = await api.post(`/admin/customers/${c.user_id}/send-invitation`);
      if (data.sent) {
        toast.success(`Invitation emailed to ${c.email}`);
      } else if (data.smtp_error && data.claim_url) {
        // iter127 — show the actual SMTP failure reason (not a generic "email failed")
        toast.error(data.smtp_error, {
          description: `Invitation link copied to clipboard: ${data.claim_url}`,
          duration: 10000,
        });
        try { await navigator.clipboard?.writeText(data.claim_url); } catch { /* clipboard may be blocked */ }
      } else if (data.claim_url) {
        toast.warning(`Email failed — copy link: ${data.claim_url}`);
      } else {
        toast.error(data.info || "Could not send invitation");
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite");
    }
  };

  const resendInvite = async (c) => {
    try {
      const { data } = await api.post(`/admin/customers/${c.user_id}/resend-invitation`);
      if (data.sent) {
        toast.success(`Invitation resent to ${c.email}`);
      } else if (data.smtp_error && data.claim_url) {
        // iter127 — surface the exact SMTP rejection reason so the admin
        // can fix it (e.g. rotate the revoked Gmail app password).
        toast.error(data.smtp_error, {
          description: `Invitation link copied to clipboard: ${data.claim_url}`,
          duration: 12000,
        });
        try { await navigator.clipboard?.writeText(data.claim_url); } catch { /* clipboard may be blocked */ }
      } else if (data.claim_url) {
        toast.warning(`Email failed — copy link: ${data.claim_url}`);
      } else {
        toast.error(data.info || "Could not resend invitation");
      }
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend invite");
    }
  };

  const archiveCustomer = async (c) => {
    if (!window.confirm(`Archive ${c.name || c.email}?\n\nThis will cancel ALL their future scheduled visits and hide them from the active list. Their history is preserved.`)) return;
    try {
      const { data } = await api.post(`/admin/customers/${c.user_id}/archive`);
      toast.success(`Archived. ${data.future_visits_cancelled} future visit(s) cancelled.`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
  };
  const unarchiveCustomer = async (c) => {
    try {
      await api.post(`/admin/customers/${c.user_id}/unarchive`);
      toast.success("Customer restored");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };
  const permanentDeleteCustomer = async (c) => {
    const name = c.name || c.email || "this customer";
    if (!window.confirm(`Move ${name} to Trash?\n\nActive bookings will be cancelled and the recurring schedule paused. You can Restore them any time from the 🗑 Deleted tab.`)) return;
    try {
      const { data } = await api.post(`/admin/customers/${c.user_id}/permanent-delete`);
      toast.success(`Moved to Trash · ${data.cancelled_bookings} booking(s) cancelled`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const restoreDeletedCustomer = async (c) => {
    try {
      await api.post(`/admin/customers/${c.user_id}/restore`);
      toast.success("Customer restored");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };
  const deleteForeverCustomer = async (c) => {
    const name = c.name || c.email || "this customer";
    // iter191 — Single-click confirmation. No typed-phrase gate that can
    // fail due to capitalization / spaces / typos. The confirm dialog
    // spells out the cascade; clicking OK is the confirmation.
    const ok = window.confirm(
      `Delete ${name} permanently?\n\n` +
      `This will:\n` +
      `• Cancel all future & recurring bookings\n` +
      `• Disable AutoPay & remove saved card\n` +
      `• Remove from calendar, routes, active list\n` +
      `• Preserve paid invoices for accounting\n\n` +
      `Click OK to Confirm Delete. This CANNOT be undone.`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/customers/${c.user_id}/delete-forever`, { force: true });
      const cancelled = data?.wiped?.future_bookings || 0;
      toast.success(
        `Customer permanently deleted. All future bookings and recurring services have been cancelled.` +
        (cancelled > 0 ? ` (${cancelled} booking${cancelled === 1 ? "" : "s"} cancelled)` : "")
      );
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      // Idempotent: treat "already deleted" / "not found" as success.
      if (detail.toLowerCase().includes("not found") || detail.toLowerCase().includes("already")) {
        toast.success("Customer was already deleted.");
        loadAll();
      } else {
        toast.error(detail || "Delete failed");
      }
    }
  };

  const openSchedule = (c) => {
    setSchedCustomer(c);
    setSchedForm({
      service_key: services?.[0]?.key || "lawn_mowing",
      square_feet: 2000,
      frequency: "one_time",
      yard_area: "both",
      preferred_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
      preferred_time: "9:00 AM",
      payment_method: "stripe",
      notes: "",
    });
    setSchedOpen(true);
  };

  const submitSchedule = async () => {
    if (!schedCustomer) return;
    if (!schedForm.preferred_date) return toast.error("Pick a date");
    if (!schedForm.service_key) return toast.error("Pick a service");
    if (!schedForm.square_feet || schedForm.square_feet < 1) return toast.error("Square footage must be > 0");
    setSchedSubmitting(true);
    try {
      const { data } = await api.post(`/admin/customers/${schedCustomer.user_id}/bookings`, schedForm);
      const isRec = ["weekly", "biweekly", "monthly"].includes(schedForm.frequency);
      toast.success(isRec ? `Booking scheduled · ${schedForm.frequency} recurring set up` : "Booking scheduled");
      setSchedOpen(false);
      // Refresh Calendar / Route Map / Upcoming Jobs
      await loadAll();
      return data;
    } catch (e) {
      // Keep the modal open + surface the actual error (interceptor already
      // normalized FastAPI validation arrays into a string).
      const msg = e?.response?.data?.detail || e?.message || "Could not schedule";
      toast.error(typeof msg === "string" ? msg : "Could not schedule");
      // eslint-disable-next-line no-console
      console.error("[Admin] Schedule submit failed:", e);
    } finally {
      setSchedSubmitting(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.patch("/admin/settings", {
        google_review_url: settings.google_review_url,
        review_delay_hours: settings.review_delay_hours,
        require_payment_before_scheduling: settings.require_payment_before_scheduling,
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSavingSettings(false);
    }
  };

  const openCancel = (b) => {
    setCancelBooking(b);
    setCancelReason("");
    setCancelOpen(true);
  };

  const submitCancel = async () => {
    if (!cancelBooking) return;
    if (cancelReason.trim().length < 2) return toast.error("Please enter a reason");
    setCancelling(true);
    try {
      await api.post(`/admin/bookings/${cancelBooking.booking_id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Booking canceled and customer notified");
      setCancelOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  const openSendQuote = (c) => {
    setQuoteCustomer(c);
    setQuoteDialogOpen(true);
  };

  const resendQuote = async (q) => {
    try {
      await api.post(`/admin/quotes/${q.quote_id}/send`);
      toast.success("Quote re-sent");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend");
    }
  };

  const acceptQuoteCounter = async (q) => {
    if (!window.confirm(`Accept ${q.customer_name}'s counter of $${Number(q.total || 0).toFixed(2)}? A booking will be created and the customer will be asked to pay.`)) return;
    try {
      await api.post(`/admin/quotes/${q.quote_id}/accept`, { message: null });
      toast.success(`Accepted at $${Number(q.total || 0).toFixed(2)}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept counter");
    }
  };

  const openQuoteCounter = async (q) => {
    const priceStr = window.prompt(
      `Counter ${q.customer_name}'s offer of $${Number(q.total || 0).toFixed(2)}\n\nEnter your counter price:`,
      String(Number(q.total || 0).toFixed(2))
    );
    if (priceStr === null) return;
    const price = parseFloat(priceStr);
    if (!price || price <= 0) { toast.error("Enter a valid price"); return; }
    const message = window.prompt("Optional note for the customer:", "") || null;
    try {
      await api.post(`/admin/quotes/${q.quote_id}/counter`, { price, message });
      toast.success(`Countered at $${price.toFixed(2)}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not counter");
    }
  };

  const rejectQuote = async (q) => {
    if (!window.confirm(`Reject ${q.customer_name}'s quote? This cannot be undone.`)) return;
    try {
      await api.post(`/admin/quotes/${q.quote_id}/cancel`);
      toast.success("Quote rejected");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject");
    }
  };

  const openCounter = (o, action) => {
    setCounterOffer(o);
    setCounterAction(action);
    setCounterPrice(action === "counter" ? String(((o.offered_price || 0) + 10).toFixed(2)) : "");
    setCounterMessage("");
    setCounterOpen(true);
  };

  const submitCounter = async () => {
    if (!counterOffer) return;
    try {
      if (counterAction === "accept") {
        await api.post(`/offers/${counterOffer.offer_id}/accept`, { message: counterMessage || null });
        toast.success(`Accepted at $${(counterOffer.offered_price || 0).toFixed(2)} — booking created`);
      } else if (counterAction === "reject") {
        await api.post(`/offers/${counterOffer.offer_id}/reject`, { message: counterMessage || null });
        toast.success("Offer rejected");
      } else if (counterAction === "counter") {
        const price = parseFloat(counterPrice);
        if (!price || price <= 0) return toast.error("Enter a counter price");
        await api.post(`/offers/${counterOffer.offer_id}/counter`, { price, message: counterMessage || null });
        toast.success(`Countered at $${price.toFixed(2)}`);
      }
      setCounterOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const totalRevenue = payments.filter(p => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#047857] font-bold">Admin Dashboard</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">JDSN Operations</h1>
          {/* iter246 — offline-first sync status pill */}
          <button
            type="button"
            onClick={() => user?.is_admin && loadAll()}
            title="Click to refresh"
            className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
              !syncMeta.online
                ? "bg-amber-50 border-amber-300 text-amber-800"
                : syncMeta.refreshing
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
            }`}
            data-testid="admin-sync-status"
          >
            {!syncMeta.online
              ? <WifiOff className="w-3 h-3" />
              : syncMeta.refreshing
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <RefreshCw className="w-3 h-3" />}
            <span data-testid="admin-sync-status-text">{syncPillText}</span>
          </button>
        </div>
        <NotificationCenter className="mt-2" />
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-7 gap-4">
        <StatCard icon={Calendar} label="Bookings" value={bookings.length} sub={`${bookings.filter(isPendingBooking).length} pending`} data-testid="stat-bookings" />
        <StatCard icon={Users} label="Customers" value={customers.length} sub="All-time" />
        <StatCard icon={FileText} label="Active Quotes" value={activeQuotes} sub={`${quotes.length} total`} />
        <StatCard icon={HandCoins} label="Active Offers" value={activeOffers} sub={`${offers.length} total`} />
        <StatCard icon={CreditCard} label="Revenue (paid)" value={`$${totalRevenue.toFixed(2)}`} sub={`${payments.filter(p=>p.payment_status==="paid").length} payments`} />
        <StatCard icon={Heart} label="Tips" value={`$${tipsTotal.toFixed(2)}`} sub={`${tips.filter(t=>t.status==="paid").length} paid`} />
        <StatCard icon={Bell} label="Notifications" value={notifications.length} sub={`${notifications.filter(n=>n.status==="queued").length} queued`} />
      </div>

      {/* iter247d — Bookings diagnostic panel. Always visible so the
          Dashboard "0 pending" investigation can be done in-page by the
          admin himself. Shows:
             * raw count from /api/admin/bookings
             * how many of those pass the shared isPendingBooking()
             * a compact tally of status strings actually seen
             * a "See raw JSON" button that opens the API response in
               a new tab (proves the number is API-side, not display).
         The panel auto-hides once there is at least one pending booking. */}
      {bookings.filter(isPendingBooking).length === 0 && (
        <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-[13px] font-semibold text-amber-900" data-testid="admin-bookings-diagnostic">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            Bookings diagnostic
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 font-mono text-[12px]">
            <div>API bookings returned: <b data-testid="diag-api-count">{bookings.length}</b></div>
            <div>Pending (isPendingBooking): <b data-testid="diag-pending-count">{bookings.filter(isPendingBooking).length}</b></div>
            <div>Terminal (cancelled/completed/etc): <b>{bookings.filter((b) => !isPendingBooking(b)).length}</b></div>
            <div>
              Source:{" "}
              <b data-testid="diag-source">
                {syncMeta.online === false
                  ? "cached (offline)"
                  : syncMeta.savedAt
                    ? "live API"
                    : "loading"}
              </b>
            </div>
          </div>
          {bookings.length > 0 && (
            <div className="mt-2 font-mono text-[11px] text-amber-800">
              Status tally:{" "}
              {(() => {
                const t = {};
                bookings.forEach((b) => {
                  const k = (b?.status == null ? "(none)" : String(b.status)).trim().toLowerCase() || "(empty)";
                  t[k] = (t[k] || 0) + 1;
                });
                return Object.entries(t)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => `${k}=${v}`)
                  .join(" · ");
              })()}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-[12px] border-amber-400 text-amber-900 hover:bg-amber-100"
              onClick={async () => {
                try {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings?t=${Date.now()}`;
                  const w = window.open("", "_blank");
                  const r = await fetch(url, { credentials: "include", cache: "no-store" });
                  const text = await r.text();
                  if (w) {
                    w.document.title = `admin/bookings (${r.status})`;
                    w.document.body.style.cssText = "font-family:ui-monospace,monospace;white-space:pre-wrap;padding:16px;";
                    w.document.body.textContent = `HTTP ${r.status}\n\n${text}`;
                  }
                } catch (e) { alert(`fetch failed: ${e}`); }
              }}
              data-testid="diag-open-raw"
            >
              Open raw /api/admin/bookings response
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-[12px] border-amber-400 text-amber-900 hover:bg-amber-100"
              onClick={async () => {
                // Force a cache-bypass refresh
                try {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings?t=${Date.now()}`;
                  const r = await fetch(url, { credentials: "include", cache: "no-store" });
                  const j = await r.json();
                  const fresh = j.bookings || [];
                  setBookings(fresh);
                  writeCache("admin:bookings", fresh);
                  setSyncMeta((m) => ({ ...m, online: true, savedAt: Date.now(), refreshing: false }));
                } catch (e) { alert(`refresh failed: ${e}`); }
              }}
              data-testid="diag-force-refresh"
            >
              Force live refresh (bypass cache)
            </Button>
          </div>
        </div>
      )}

      <Tabs value={tabParam} onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })} className="mt-8" data-testid="admin-tabs">
        <TabsList className="bg-white border border-gray-200 rounded-full p-1 flex flex-wrap h-auto">
          <TabsTrigger value="bookings" className="rounded-full" data-testid="tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="pending" className="rounded-full" data-testid="tab-pending">Pending Payments</TabsTrigger>
          <TabsTrigger value="calendar" className="rounded-full" data-testid="tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="route" className="rounded-full" data-testid="tab-route">Route Map</TabsTrigger>
          <TabsTrigger value="quotes" className="rounded-full" data-testid="tab-quotes">Quotes {activeQuotes > 0 && <span className="ml-1 inline-flex items-center justify-center text-[10px] font-extrabold bg-emerald-100 text-[#047857] rounded-full px-1.5 min-w-[18px] h-[18px]">{activeQuotes}</span>}</TabsTrigger>
          <TabsTrigger value="offers" className="rounded-full" data-testid="tab-offers">Offers {activeOffers > 0 && <span className="ml-1 inline-flex items-center justify-center text-[10px] font-extrabold bg-amber-100 text-amber-800 rounded-full px-1.5 min-w-[18px] h-[18px]">{activeOffers}</span>}</TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-full" data-testid="tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="estimates" className="rounded-full" data-testid="tab-estimates">Estimates</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-full" data-testid="tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="signups" className="rounded-full" data-testid="tab-signups">Abandoned</TabsTrigger>
          <TabsTrigger value="leads" className="rounded-full" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="tips" className="rounded-full" data-testid="tab-tips">Tips</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-full" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="messages" className="rounded-full" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="campaigns" className="rounded-full" data-testid="tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-full" data-testid="tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-full" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-5 space-y-6">
          <IncompleteBookingsPanel />
          {/* Dashboard summary cards */}
          <DashboardSummary />

          {/* Nearby Customer Opportunities — fills empty slots on existing routes */}
          <NearbyOpportunitiesCard initialDate={todayDate} onScheduled={() => loadAll()} />

          {/* Today's Jobs route view */}
          <div className="bg-gradient-to-br from-[#064E3B] to-[#047857] text-white rounded-2xl p-5" data-testid="today-jobs-card">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Sun className="w-4.5 h-4.5 text-amber-300" />
                <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-100">Today's Jobs · {todayDate}</div>
              </div>
              <Button
                size="sm"
                onClick={startRoute}
                disabled={startingRoute || todayJobs.length === 0}
                data-testid="start-route-btn"
                className="rounded-full bg-amber-400 hover:bg-amber-300 text-[#1f1300] font-extrabold text-[11px] h-8 px-3"
              >
                {startingRoute ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "🚐 Start Route"}
              </Button>
            </div>
            <div className="mt-1 text-2xl font-extrabold tracking-tighter font-[Outfit]">
              {todayJobs.length === 0 ? "No jobs scheduled today" : `${todayJobs.length} ${todayJobs.length === 1 ? "job" : "jobs"} on the route`}
            </div>
            {todayJobs.length > 0 && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {todayJobs.map((j) => (
                  <div key={j.booking_id} className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/15" data-testid={`today-job-${j.booking_id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <div className="text-[11px] uppercase tracking-[0.15em] text-emerald-200 font-bold">{j.preferred_time}</div>
                          <StatusBadge status={j.status} />
                          <PaymentBadge paymentStatus={j.payment_status}
                                         bookingId={j.booking_id}
                                         onMarkPaid={async (id) => {
                                           try {
                                             await api.post(`/admin/bookings/${id}/mark-paid`);
                                             toast.success("Marked as Paid");
                                             loadAll();
                                           } catch (e) {
                                             toast.error(e?.response?.data?.detail || "Could not mark as paid");
                                           }
                                         }} />
                        </div>
                        <div className="font-bold truncate">{j.service_name}</div>
                        <div className="text-xs text-emerald-100/90 truncate">{j.name} · {j.phone}</div>
                        <div className="text-xs text-emerald-100/80 truncate">{j.address}</div>
                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                          <GetDirectionsButton address={j.address} />
                          {j.checkin_at && <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-amber-200">🚐 Checked in</div>}
                          {j.customer_confirmed_at && <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-emerald-200">✓ Customer confirmed</div>}
                          {j.reschedule_requested_at && <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-rose-200">⚠ Reschedule requested</div>}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1.5">
                        <div className="flex items-center gap-1">
                          <div className="text-lg font-extrabold tabular-nums">${j.price?.toFixed(2)}</div>
                          <JobActionsMenu booking={j} onUpdated={loadAll} />
                        </div>
                        {!j.checkin_at && (
                          <Button
                            size="sm"
                            onClick={() => checkIn(j)}
                            disabled={checkingIn === j.booking_id}
                            data-testid={`today-checkin-${j.booking_id}`}
                            className="rounded-full bg-amber-300 hover:bg-amber-200 text-[#1f1300] font-extrabold text-[11px] h-8 px-3"
                          >
                            {checkingIn === j.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "📍 Check In"}
                          </Button>
                        )}
                        {j.checkin_at && !j.checkout_at && (
                          <Button
                            size="sm"
                            onClick={() => checkOut(j)}
                            disabled={checkingOut === j.booking_id}
                            data-testid={`today-checkout-${j.booking_id}`}
                            className="rounded-full bg-emerald-300 hover:bg-emerald-200 text-[#062818] font-extrabold text-[11px] h-8 px-3"
                          >
                            {checkingOut === j.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "✅ Check Out"}
                          </Button>
                        )}
                        {!j.checkin_at && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full text-white border-white/30 hover:bg-white/10 font-bold text-[11px] h-7 px-2"
                            onClick={() => markComplete(j)}
                            disabled={completing === j.booking_id}
                            data-testid={`today-complete-${j.booking_id}`}
                          >
                            {completing === j.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Mark Complete"}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(() => {
            const sourceFiltered = bookingSourceFilter
              ? bookings.filter((b) => (b.source || "direct") === bookingSourceFilter)
              : bookings;
            // iter247 — Use the SAME `isPendingBooking` helper the
            // dashboard card uses, so "Hide cancelled/completed" filters
            // and the dashboard's Pending count agree on the same set.
            const visibleBookings = hideCancelledJobs
              ? sourceFiltered.filter(isPendingBooking)
              : sourceFiltered;
            const hiddenCount = sourceFiltered.length - visibleBookings.length;
            // Count how many bookings we have per source so the chips show a badge.
            const sourceCounts = bookings.reduce((acc, b) => {
              const k = b.source || "direct";
              acc[k] = (acc[k] || 0) + 1;
              return acc;
            }, {});
            return (
              <>
                <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="booking-source-filter">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mr-1">Source</span>
                  <button
                    type="button"
                    onClick={() => setBookingSourceFilter("")}
                    data-testid="booking-source-all"
                    className={`text-[11px] font-extrabold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border transition ${
                      bookingSourceFilter === ""
                        ? "bg-[#047857] text-white border-[#047857]"
                        : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300"
                    }`}
                  >
                    All ({bookings.length})
                  </button>
                  {LEAD_SOURCES.filter((s) => sourceCounts[s]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setBookingSourceFilter(s)}
                      data-testid={`booking-source-${s}`}
                      className={`text-[11px] font-extrabold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border transition ${
                        bookingSourceFilter === s
                          ? "bg-[#047857] text-white border-[#047857]"
                          : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300"
                      }`}
                    >
                      {LEAD_SOURCE_META[s].label} ({sourceCounts[s]})
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="hide-cancelled-toggle-wrap">
                    <input
                      type="checkbox"
                      checked={hideCancelledJobs}
                      onChange={(e) => setHideCancelledJobs(e.target.checked)}
                      data-testid="hide-cancelled-toggle"
                      className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-[13px] font-bold text-gray-800">Hide cancelled jobs</span>
                    {hiddenCount > 0 && (
                      <span className="text-[11px] text-gray-500">({hiddenCount} hidden)</span>
                    )}
                  </label>
                </div>
                {visibleBookings.length === 0 ? <Empty label={bookingSourceFilter && sourceFiltered.length === 0 ? `No bookings from ${LEAD_SOURCE_META[bookingSourceFilter]?.label || bookingSourceFilter} yet` : hideCancelledJobs && bookings.length ? "No active bookings — toggle off to see cancelled" : "No bookings yet"} /> : visibleBookings.map((b) => {
                  const recurring = RECURRING_FREQS.has(b.frequency);
                  const canComplete = b.status !== "completed" && b.status !== "cancelled";
                  return (
            <div key={b.booking_id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`admin-booking-${b.booking_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-gray-900">{b.service_name} <span className="text-gray-500 font-normal">· #{b.booking_id.slice(-6)}</span></div>
                    <SourceBadge source={b.source || "direct"} />
                    {b.property_label && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em] rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"
                            data-testid={`admin-booking-property-${b.booking_id}`}>
                        🏠 {b.property_label}
                      </span>
                    )}
                  </div>
                  {Array.isArray(b.additional_services) && b.additional_services.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1" data-testid={`admin-addons-${b.booking_id}`}>
                      {b.additional_services.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.1em] rounded-full bg-emerald-50 text-[#047857] border border-emerald-200">
                          + {a.service_name}{a.price ? ` · $${a.price.toFixed(0)}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="text-sm text-gray-700 mt-1">{b.name} · {b.phone} · {b.email}</div>
                  <div className="text-sm text-gray-600">{b.address} · {b.area}</div>
                  <div className="text-sm text-gray-600">{b.preferred_date} {b.preferred_time}</div>
                  <div className="flex items-center gap-2 flex-wrap mt-1.5">
                    {b.frequency_label && (
                      <span className={`inline-flex items-center text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${b.calc?.frequency_delta < 0 ? "bg-emerald-100 text-[#047857]" : b.calc?.frequency_delta > 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-700"}`}>
                        {b.frequency_label}
                      </span>
                    )}
                    {recurring && (
                      <span className="inline-flex items-center text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Recurring</span>
                    )}
                    {b.long_grass_fee?.amount > 0 && (
                      <span
                        data-testid={`admin-lg-chip-${b.booking_id}`}
                        className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                          b.long_grass_fee.paid_at
                            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                            : b.long_grass_fee.customer_approval === "approved"
                              ? "bg-amber-50 text-amber-800 border-amber-200"
                              : b.long_grass_fee.customer_approval === "rejected"
                                ? "bg-rose-50 text-rose-800 border-rose-200"
                                : "bg-amber-50 text-amber-800 border-amber-200"
                        }`}
                      >
                        LG +${b.long_grass_fee.amount.toFixed(0)} · {b.long_grass_fee.paid_at ? "paid" : (b.long_grass_fee.customer_approval || "pending")}
                      </span>
                    )}
                    {b.payment_status === "payment_failed" && (
                      <span className="inline-flex items-center text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">Auto-charge failed</span>
                    )}
                    {b.parent_booking_id && (
                      <span className="inline-flex items-center text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-emerald-50 text-[#047857] border border-emerald-200">Auto-created</span>
                    )}
                    {/* iter105 — Feedback status badges (Review / Like / Tip). */}
                    <FeedbackStatusBadge kind="review" booking={b} />
                    <FeedbackStatusBadge kind="like" booking={b} />
                    <FeedbackStatusBadge kind="tip" booking={b} />
                    <PaymentMethodChip method={b.payment_method} methodLabel={b.payment_method_label} />
                    <BookingSqftVerifyButton booking={b} onUpdated={loadAll} />
                    {b.mowable_confidence && (
                      <ConfidenceChip confidence={b.mowable_confidence} pct={b.mowable_confidence_pct} />
                    )}
                    {(b.lat || b.address) && (
                      <button
                        type="button"
                        onClick={() => setMapBooking(b)}
                        data-testid={`view-property-map-${b.booking_id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.12em] px-2 py-1 rounded-full border bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50 transition-colors"
                        title="View property map (admin-only)"
                      >
                        <MapPin className="w-3 h-3" /> View Map
                      </button>
                    )}
                  </div>
                  {b.payment_status === "paid" && b.stripe_payment_intent_id && (
                    <div className="text-[10px] text-gray-500 mt-1 font-mono" data-testid={`admin-booking-txn-${b.booking_id}`}>
                      Txn: {String(b.stripe_payment_intent_id).slice(-12)}
                    </div>
                  )}
                  {b.last_payment_reminder_at && (
                    <div className="mt-1.5">
                      <LastReminderChip booking={b} />
                    </div>
                  )}
                  {/* iter74/82: AutoPay + Card on File indicators. Admin
                      can now also remove a customer's saved card inline —
                      useful for support / fraud / customer-request cases. */}
                  {(b.customer_has_card_on_file || b.customer_autopay_enabled) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {b.customer_has_card_on_file && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-violet-50 text-violet-800 border-violet-200"
                          data-testid={`card-on-file-pill-${b.booking_id}`}
                        >
                          <CreditCard className="w-3 h-3" />
                          {(b.customer_card_brand ? String(b.customer_card_brand).toUpperCase() : "CARD")}
                          {b.customer_card_last4 && ` •••• ${b.customer_card_last4}`}
                        </span>
                      )}
                      {/* iter197 — live AutoPay status pill on the customer card */}
                      {b.user_id && (
                        <AutoPayEnrollmentControl
                          userId={b.user_id}
                          compact
                          testIdPrefix={`customer-card-${b.booking_id}`}
                        />
                      )}
                      {b.customer_has_card_on_file && b.user_id && (
                        <button
                          type="button"
                          onClick={() => removeCustomerSavedCard(b)}
                          className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100"
                          data-testid={`remove-saved-card-${b.booking_id}`}
                          title="Remove saved card (Stripe detach)"
                        >
                          <Trash2 className="w-3 h-3" /> Remove Card
                        </button>
                      )}
                    </div>
                  )}
                  {b.notes && <div className="text-xs text-gray-500 mt-1 italic">"{b.notes}"</div>}
                </div>
                <div className="flex flex-col gap-2 min-w-[200px]">
                  <div className="text-2xl font-extrabold text-[#047857] tabular-nums">${b.price?.toFixed(2)}</div>
                  <Select value={b.status} onValueChange={(v) => updateBooking(b.booking_id, { status: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={b.payment_status} onValueChange={(v) => updateBooking(b.booking_id, { payment_status: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  {canComplete && (
                    <Button
                      size="sm"
                      className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-bold"
                      onClick={() => markComplete(b)}
                      disabled={completing === b.booking_id}
                      data-testid={`mark-complete-${b.booking_id}`}
                    >
                      {completing === b.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Complete</>}
                    </Button>
                  )}
                  {b.payment_status === "payment_pending" && (
                    <Button
                      size="sm"
                      className="rounded-full bg-rose-600 hover:bg-rose-700 text-white font-bold"
                      onClick={() => retryPayment(b)}
                      data-testid={`retry-payment-${b.booking_id}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry Payment
                    </Button>
                  )}
                  {/* iter95: 🔔 Payment Reminder button — visible on every
                      unpaid card (pending checkout OR failed auto-charge on
                      completion). Fires SMS + email + copies checkout link. */}
                  {(b.payment_status === "payment_pending" ||
                    b.payment_status === "unpaid" ||
                    b.payment_status === "pending_verification" ||
                    b.payment_status === "initiated") && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-50 font-bold"
                      onClick={() => remindToPayInline(b)}
                      data-testid={`send-payment-reminder-${b.booking_id}`}
                    >
                      <Bell className="w-3.5 h-3.5 mr-1" /> Payment Reminder
                    </Button>
                  )}
                  {canComplete && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50"
                      onClick={() => openCancel(b)}
                      data-testid={`cancel-job-${b.booking_id}`}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel Job
                    </Button>
                  )}
                  {/* Full actions menu — Change Payment Method, Remind to Pay,
                      Update Price, etc. (iter73) */}
                  <div className="flex justify-end">
                    <JobActionsMenu booking={b} onUpdated={loadAll} />
                  </div>
                  {b.cancellation_reason && (
                    <div className="text-[10px] text-rose-700 bg-rose-50 border border-rose-100 rounded p-1.5">
                      Canceled: {b.cancellation_reason}
                    </div>
                  )}
                  <LastReminderChip booking={b} />
                </div>
              </div>
            </div>
          )})}
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="pending" className="mt-5 space-y-4">
          <PendingPaymentsPanel />
        </TabsContent>


        <TabsContent value="calendar" className="mt-5 space-y-4">
          <DashboardSummary />
          <CalendarMonthView />
        </TabsContent>

        <TabsContent value="route" className="mt-5 space-y-4">
          <RouteMapPanel />
        </TabsContent>

        <TabsContent value="quotes" className="mt-5 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {["draft","sent","viewed","accepted","rejected","changes_requested","counter_offered","expired"].map((s) => (
              <span key={s} className="inline-flex items-center text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                {s.replace(/_/g, " ")}: {quotesCounts[s] || 0}
              </span>
            ))}
          </div>
          {quotes.length === 0 ? (
            <Empty label="No quotes yet — pick a customer and click Send Quote" />
          ) : quotes.map((q) => {
            const STATUS_STYLES = {
              draft: "bg-gray-100 text-gray-700",
              sent: "bg-sky-100 text-sky-800",
              viewed: "bg-sky-100 text-sky-800",
              accepted: "bg-emerald-100 text-emerald-800",
              rejected: "bg-rose-100 text-rose-800",
              changes_requested: "bg-amber-100 text-amber-800",
              counter_offered: "bg-fuchsia-100 text-fuchsia-800",
              expired: "bg-gray-200 text-gray-600",
            };
            const isCounterAwaitingAdmin = q.status === "counter_offered" && q.last_action_by === "customer";
            return (
            <div key={q.quote_id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`admin-quote-${q.quote_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                    <FileText className="w-4 h-4 text-[#047857]" />
                    {q.title} <span className="text-gray-500 font-normal">· #{q.quote_id.slice(-6)}</span>
                  </div>
                  <div className="text-sm text-gray-700 mt-1">{q.customer_name}{q.customer_email && ` · ${q.customer_email}`}</div>
                  {q.address && <div className="text-xs text-gray-500">{q.address}</div>}
                  <div className="text-xs text-gray-500 mt-1">{(q.items || []).length} line item{q.items?.length === 1 ? "" : "s"} · Expires {q.expires_at?.split("T")[0]}</div>
                  {q.customer_message && <div className="text-xs text-gray-500 mt-1 italic">"{q.customer_message}"</div>}
                  {(q.counter_count || 0) > 0 && (
                    <div className="mt-1.5 flex items-center gap-2 text-[11px] font-bold text-fuchsia-700">
                      Round {q.counter_count}/5 · Last: {q.last_action_by === "customer" ? "Customer" : "You"} @ ${Number(q.last_counter_price || q.total || 0).toFixed(2)}
                      {q.original_total ? <span className="text-gray-500 font-normal">(orig ${Number(q.original_total).toFixed(2)})</span> : null}
                    </div>
                  )}
                  {q.last_counter_message && (
                    <div className="text-xs text-gray-600 mt-1">Note: "{q.last_counter_message}"</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 min-w-[160px]">
                  <div className="text-2xl font-extrabold text-[#047857] tabular-nums">${q.total?.toFixed(2)}</div>
                  <span className={`text-[10px] uppercase tracking-[0.14em] font-extrabold px-2 py-0.5 rounded-full ${STATUS_STYLES[q.status] || "bg-gray-100 text-gray-700"}`}>
                    {(q.status || "draft").replace(/_/g, " ")}
                  </span>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.open(`/quote/${q.quote_id}`, "_blank")} data-testid={`quote-view-${q.quote_id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                    {(q.status === "draft" || q.status === "changes_requested") && (
                      <Button size="sm" className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-bold" onClick={() => resendQuote(q)} data-testid={`quote-send-${q.quote_id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" /> Send
                      </Button>
                    )}
                    {isCounterAwaitingAdmin && (
                      <>
                        <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={() => acceptQuoteCounter(q)} data-testid={`quote-accept-counter-${q.quote_id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Accept ${Number(q.total || 0).toFixed(2)}
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full font-bold border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-50" onClick={() => openQuoteCounter(q)} data-testid={`quote-counter-${q.quote_id}`}>
                          Counter back
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => rejectQuote(q)} data-testid={`quote-reject-${q.quote_id}`}>
                          Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )})}
        </TabsContent>

        <TabsContent value="offers" className="mt-5 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {["pending","counter_offered","accepted","rejected","expired"].map((s) => (
              <span key={s} className="inline-flex items-center text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                {s.replace(/_/g, " ")}: {offersCounts[s] || 0}
              </span>
            ))}
          </div>
          {offers.length === 0 ? <Empty label="No offers yet — customers can negotiate from the estimate page" /> : offers.map((o) => {
            const OSTAT = {
              pending: "bg-amber-100 text-amber-800",
              counter_offered: "bg-sky-100 text-sky-800",
              accepted: "bg-emerald-100 text-emerald-800",
              rejected: "bg-rose-100 text-rose-800",
              expired: "bg-gray-200 text-gray-600",
            };
            const isOpen = !["accepted","rejected","expired"].includes(o.status);
            const ourTurn = isOpen && o.last_action_by === "customer";
            return (
              <div key={o.offer_id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`admin-offer-${o.offer_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                      <HandCoins className="w-4 h-4 text-[#047857]" />
                      {o.customer_name} <span className="text-gray-500 font-normal">· {o.service_name || o.service_key} · #{o.offer_id.slice(-6)}</span>
                    </div>
                    <div className="text-sm text-gray-700 mt-1">{o.customer_phone} {o.customer_email && `· ${o.customer_email}`}</div>
                    {o.address && <div className="text-xs text-gray-500">{o.address}</div>}
                    <div className="text-xs text-gray-500 mt-1">
                      {o.quote_low != null && o.quote_high != null ? (
                        <>Range <span className="font-bold tabular-nums">${(o.quote_low).toFixed(0)}–${(o.quote_high).toFixed(0)}</span> · </>
                      ) : (
                        <>Estimated <span className="font-bold tabular-nums">${(o.estimated_price || 0).toFixed(2)}</span> · </>
                      )}
                      &nbsp;Round {o.counter_count || 0}/{5}
                      {o.preferred_date && ` · Wants ${o.preferred_date} ${o.preferred_time || ""}`}
                    </div>
                    {o.quote_description && <div className="text-xs text-emerald-700 font-bold mt-1">{o.quote_description}</div>}
                    {o.notes && <div className="text-xs text-gray-500 mt-1 italic">"{o.notes}"</div>}
                    {o.last_counter_message && <div className="text-xs text-gray-600 mt-1">Last message: "{o.last_counter_message}"</div>}
                  </div>
                  <div className="flex flex-col items-end gap-2 min-w-[200px]">
                    <div className="text-2xl font-extrabold text-[#047857] tabular-nums">${(o.offered_price || 0).toFixed(2)}</div>
                    <span className={`text-[10px] uppercase tracking-[0.14em] font-extrabold px-2 py-0.5 rounded-full ${OSTAT[o.status]}`}>
                      {(o.status || "pending").replace(/_/g, " ")}
                      {ourTurn && " · your turn"}
                    </span>
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.open(`/offer/${o.offer_id}`, "_blank")} data-testid={`offer-view-${o.offer_id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                      {isOpen && ourTurn && (
                        <>
                          <Button size="sm" className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-bold" onClick={() => openCounter(o, "accept")} data-testid={`offer-accept-${o.offer_id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Accept ${(o.offered_price || 0).toFixed(2)}
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => openCounter(o, "counter")} data-testid={`offer-counter-${o.offer_id}`}>
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Counter
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => openCounter(o, "reject")} data-testid={`offer-reject-${o.offer_id}`}>
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="notifications" className="mt-5 space-y-3">
          {(!notifStatus.smtp_configured || !notifStatus.sms_configured) && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="notif-banner">
              <div className="font-bold mb-1">Connect live delivery to start sending automatically</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {!notifStatus.smtp_configured && (
                  <li><span className="font-semibold">Email:</span> add <code className="bg-amber-100 px-1 rounded">SMTP_PASS</code> (Gmail app password) to <code className="bg-amber-100 px-1 rounded">backend/.env</code> then restart backend. Currently emails are queued only.</li>
                )}
                {!notifStatus.sms_configured && (
                  <li><span className="font-semibold">SMS:</span> add Twilio keys (TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM) to enable text delivery. Until then SMS notifications stay queued (visible below).</li>
                )}
              </ul>
            </div>
          )}
          {notifications.length === 0 ? <Empty label="No notifications yet" /> : notifications.map((n) => {
            const statusColors = n.status === "sent" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : n.status === "failed" ? "bg-rose-100 text-rose-800 border-rose-200"
              : "bg-amber-100 text-amber-800 border-amber-200";
            const Icon = n.channel === "email" ? Mail : Smartphone;
            return (
              <div key={n.notification_id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`admin-notif-${n.notification_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 flex-1 min-w-[260px]">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${n.channel === "email" ? "bg-emerald-100 text-[#047857]" : "bg-sky-100 text-sky-700"}`}>
                      <Icon className="w-4.5 h-4.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500">{n.channel} · {n.audience}</span>
                        <span className={`px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider rounded-full border ${statusColors}`}>{n.status}</span>
                      </div>
                      <div className="mt-1 font-bold text-gray-900 truncate">{n.subject}</div>
                      <div className="mt-0.5 text-xs text-gray-500 truncate">To: {n.to}</div>
                      <pre className="mt-2 text-[12px] text-gray-700 whitespace-pre-wrap font-sans bg-gray-50 rounded-xl p-3 max-h-32 overflow-auto">{n.body}</pre>
                      {n.info && <div className="mt-1 text-[10px] text-gray-400">{n.info}</div>}
                    </div>
                  </div>
                  {n.channel === "email" && (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => resendNotification(n.notification_id)} data-testid={`resend-${n.notification_id}`}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Resend
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="estimates" className="mt-5 space-y-3">
          {estimates.length === 0 ? <Empty label="No estimates yet" /> : estimates.map((e) => (
            <div key={e.estimate_id} className="bg-white rounded-2xl border border-gray-200 p-5 flex justify-between flex-wrap gap-3" data-testid={`admin-estimate-${e.estimate_id}`}>
              <div>
                <div className="font-bold text-gray-900">{e.calc?.service_name}</div>
                <div className="text-sm text-gray-700">{e.name} · {e.phone} · {e.email}</div>
                <div className="text-xs text-gray-500">{e.address}</div>
              </div>
              <div className="text-xl font-extrabold text-[#047857] tabular-nums">${e.calc?.total?.toFixed(2)}</div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="customers" className="mt-5 space-y-3">
          {/* Customer conversion stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5" data-testid="conversion-stats">
            <ConvCard label="Invitations" value={convStats.invitations_sent || 0} sub="sent total" testid="conv-invites" />
            <ConvCard label="Activated" value={convStats.activated || 0} sub="accounts" testid="conv-activated" />
            <ConvCard label="Activation Rate" value={`${convStats.activation_rate || 0}%`} sub="of invitations" testid="conv-rate" />
            <ConvCard label="First-Time" value={convStats.first_time || 0} sub="customers" testid="conv-first" />
            <ConvCard label="Recurring" value={convStats.recurring || 0} sub="active subs" testid="conv-recurring" />
          </div>

          <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm text-gray-600">{filteredCustomers.length} customer{filteredCustomers.length === 1 ? "" : "s"}</div>
              <div className="flex items-center gap-1 flex-wrap" data-testid="customer-status-filter">
                {[
                  { key: "active",    label: "Active" },
                  { key: "recurring", label: "Recurring" },
                  { key: "one_time",  label: "One-Time" },
                  { key: "cancelled", label: "Cancelled" },
                  { key: "archived",  label: "Archived" },
                  { key: "deleted",   label: "🗑 Deleted" },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setCustomerStatusFilter(f.key)}
                    data-testid={`status-filter-${f.key}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold border transition ${customerStatusFilter === f.key ? "bg-[#047857] text-white border-[#047857]" : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300"}`}
                  >{f.label}</button>
                ))}
              </div>
            </div>
            <Button
              onClick={() => setAddCustOpen(true)}
              className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-bold"
              data-testid="add-customer-btn"
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> Add Customer
            </Button>
          </div>

          {/* Instant customer search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Search by name, email, phone, or address…"
              className="pl-9 rounded-full h-10"
              data-testid="customer-search-input"
            />
            {customerSearch && (
              <button type="button" onClick={() => setCustomerSearch("")}
                      data-testid="customer-search-clear"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-sm font-bold">
                ✕
              </button>
            )}
          </div>

          {filteredCustomers.length === 0 ? (
            <Empty label={customerSearch ? `No matches for "${customerSearch}"` : "No customers yet"} />
          ) : filteredCustomers.map((c) => {
            const cBookings = bookings.filter(bk => bk.user_id === c.user_id);
            const firstPaid = cBookings.some(bk => bk.payment_status === "paid");
            const isActivated = !c.crm_only && !c.is_admin && c.invitation_status === "active_account";
            const hasInviteSent = (c.invitation_send_count || 0) > 0;
            return (
            <div key={c.user_id} className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3" data-testid={`admin-customer-${c.user_id}`}>
              <div className="flex items-center gap-4 flex-wrap">
                {c.picture ? <img src={c.picture} alt="" className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full bg-[#047857]/10 text-[#047857] font-bold flex items-center justify-center">{(c.name||c.email||"?")[0].toUpperCase()}</div>}
                <div className="flex-1 min-w-[200px]">
                  <div className="font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                    {c.name || "—"}
                    {c.is_admin && <span className="text-[10px] uppercase font-bold tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">Admin</span>}
                    {!c.is_admin && <InvitationStatusBadge customer={c} />}
                    {c.recurring_schedule?.frequency && c.recurring_schedule.frequency !== "one_time" && (
                      <span className="text-[10px] uppercase font-bold tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`recurring-badge-${c.user_id}`}>
                        {c.recurring_schedule.frequency_label || c.recurring_schedule.frequency}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">{c.email || "no email"} {c.phone && `· ${c.phone}`}</div>
                  {c.default_address && <div className="text-xs text-gray-500 mt-0.5">{c.default_address}</div>}
                  {c.recurring_schedule?.next_service_date && (
                    <div className="text-[11px] text-emerald-700 font-bold mt-0.5">Next service: {c.recurring_schedule.next_service_date}</div>
                  )}
                  {c.recurring_schedule?.payment_method && (
                    <div className="mt-1.5"><PaymentMethodChip method={c.recurring_schedule.payment_method} /></div>
                  )}
                  {c.notes && <div className="text-xs text-gray-500 mt-0.5 italic">"{c.notes}"</div>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-bold"
                    onClick={() => window.location.assign(`/admin/customers/${c.user_id}/bookings`)}
                    data-testid={`open-bookings-${c.user_id}`}
                  >
                    <ClipboardList className="w-3.5 h-3.5 mr-1" /> Bookings
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => openSchedule(c)} data-testid={`schedule-${c.user_id}`}>
                    <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Schedule
                  </Button>
                  <Button size="sm" className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-bold" onClick={() => openSendQuote(c)} data-testid={`send-quote-${c.user_id}`}>
                    <FileText className="w-3.5 h-3.5 mr-1" /> Send Quote
                  </Button>
                  {!isActivated && c.email && !hasInviteSent && (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => sendInvite(c)} data-testid={`invite-${c.user_id}`}>
                      <Send className="w-3.5 h-3.5 mr-1" /> Send Invitation
                    </Button>
                  )}
                  {!isActivated && c.email && hasInviteSent && (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => resendInvite(c)} data-testid={`resend-invite-${c.user_id}`}>
                      <Send className="w-3.5 h-3.5 mr-1" /> Resend Invitation
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => setEditCust(c)}
                    data-testid={`edit-customer-${c.user_id}`}
                    aria-label="Edit customer"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {!c.is_admin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-violet-300 text-violet-700 hover:bg-violet-50"
                      onClick={() => setPmDialogCust(c)}
                      data-testid={`change-payment-method-${c.user_id}`}
                      aria-label="Change payment method"
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1" /> Payment
                    </Button>
                  )}
                  {!c.is_admin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => setPricingCustomer(c)}
                      data-testid={`update-price-${c.user_id}`}
                      aria-label="Update customer price"
                    >
                      <DollarSign className="w-3.5 h-3.5 mr-1" /> Update Price
                    </Button>
                  )}
                  {!c.is_admin && !c.archived && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => archiveCustomer(c)}
                      data-testid={`archive-customer-${c.user_id}`}
                      aria-label="Archive customer"
                    >
                      <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                    </Button>
                  )}
                  {c.archived && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => unarchiveCustomer(c)}
                      data-testid={`unarchive-customer-${c.user_id}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Restore
                    </Button>
                  )}
                  {c.deleted_at ? (
                    <>
                      <Button
                        size="sm"
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold"
                        onClick={() => restoreDeletedCustomer(c)}
                        data-testid={`restore-deleted-customer-${c.user_id}`}
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Restore
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full border-rose-500 text-rose-700 hover:bg-rose-50 font-extrabold"
                        onClick={() => deleteForeverCustomer(c)}
                        data-testid={`delete-forever-customer-${c.user_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Forever
                      </Button>
                    </>
                  ) : !c.is_admin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full border-rose-400 text-rose-700 hover:bg-rose-50"
                      onClick={() => permanentDeleteCustomer(c)}
                      data-testid={`delete-customer-${c.user_id}`}
                      aria-label="Move customer to Trash"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              {c.deleted_at && (
                <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px] text-gray-600 bg-rose-50/60 border border-rose-100 rounded-xl px-3 py-1.5"
                     data-testid={`deleted-meta-${c.user_id}`}>
                  <span className="font-extrabold text-rose-800">🗑 Deleted</span>
                  <span>· {new Date(c.deleted_at).toLocaleString()}</span>
                  {c.deleted_by && <span>· by {c.deleted_by}</span>}
                </div>
              )}
              {!c.is_admin && !c.deleted_at && (
                <>
                  {/* iter205 — AutoPay enrollment surfaced on the top-level
                      Customers list (owner ask 2026-03-01). Same expanded
                      control as the profile drill-down page, so the admin
                      can Enable / Disable AutoPay + see Card on File
                      without opening the Bookings drill-down first. */}
                  <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50/50 px-3 py-2.5"
                       data-testid={`customer-card-autopay-${c.user_id}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-700 mb-1.5">
                      AutoPay
                    </div>
                    <AutoPayEnrollmentControl
                      userId={c.user_id}
                      onChange={loadAll}
                      testIdPrefix={`customer-card-autopay-${c.user_id}`}
                    />
                  </div>
                  {/* iter255 (2026-08-24) — Multi-Property panel per customer.
                      Admin sees + manages all properties inline. */}
                  <AdminPropertiesPanel userId={c.user_id} />
                  <InvitationProgressTracker customer={c} bookingCount={cBookings.length} firstPaid={firstPaid} />
                </>
              )}
            </div>
          );})}
        </TabsContent>

        <TabsContent value="tips" className="mt-5 space-y-3">
          <div className="bg-gradient-to-br from-rose-500 to-pink-600 text-white rounded-2xl p-5">
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-white" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-rose-100">Total Tips Received</div>
            </div>
            <div className="mt-1 text-3xl font-extrabold font-[Outfit] tracking-tighter">${tipsTotal.toFixed(2)}</div>
            <div className="text-sm text-rose-100">{tips.filter(t => t.status === "paid").length} paid tips · {tips.length} total interactions</div>
          </div>
          {tips.length === 0 ? <Empty label="No tips yet — customers will see the tip prompt after Mark Complete" /> : tips.map((t) => (
            <div key={t.tip_id} className="bg-white rounded-2xl border border-gray-200 p-5 flex justify-between flex-wrap gap-3" data-testid={`admin-tip-${t.tip_id}`}>
              <div>
                <div className="font-bold text-gray-900">{t.customer_name || "Customer"} · Booking #{t.booking_id?.slice(-6)}</div>
                <div className="text-xs text-gray-500">{t.created_at?.split("T")[0]} · {t.method || "—"}</div>
                {t.info && <div className="text-[10px] text-gray-400 mt-0.5">{t.info}</div>}
              </div>
              <div className="text-right">
                <div className="text-2xl font-extrabold text-rose-600 tabular-nums">${Number(t.amount).toFixed(2)}</div>
                <span className={`text-[10px] uppercase font-extrabold tracking-wider px-2 py-0.5 rounded-full ${t.status === "paid" ? "bg-emerald-100 text-emerald-800" : t.status === "failed" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{t.status}</span>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="payments" className="mt-5 space-y-4">
          <PaymentMethodReports />
          <RetentionStatsCard />
          <ReviewsTipsDashboardCard />
          {payments.length === 0 ? <Empty label="No payments yet" /> : payments.map((p) => (
            <div key={p.session_id} className="bg-white rounded-2xl border border-gray-200 p-5 flex justify-between gap-4 flex-wrap" data-testid={`admin-payment-${p.session_id}`}>
              <div>
                <div className="font-bold text-gray-900">${p.amount?.toFixed(2)} {p.currency?.toUpperCase()}</div>
                <div className="text-xs text-gray-500">Booking: {p.booking_id}</div>
                <div className="text-xs text-gray-500">{p.created_at?.split("T")[0]}</div>
                <div className="mt-1.5"><PaymentMethodChip method={p.payment_method || "stripe"} /></div>
                {p.stripe_payment_intent_id && (
                  <div className="text-[10px] text-gray-500 mt-1 font-mono" data-testid={`admin-payment-txn-${p.session_id}`}>
                    Txn: {String(p.stripe_payment_intent_id).slice(-12)}
                  </div>
                )}
              </div>
              <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full border ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{p.payment_status}</span>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="messages" className="mt-5 space-y-3">
          {messages.length === 0 ? <Empty label="No messages yet" /> : messages.map((m) => (
            <div key={m.message_id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`admin-msg-${m.message_id}`}>
              <div className="font-bold text-gray-900">{m.name} <span className="font-normal text-gray-600">· {m.phone}</span></div>
              {m.email && <div className="text-xs text-gray-500">{m.email}</div>}
              <p className="mt-2 text-gray-800 text-sm">{m.message}</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="campaigns" className="mt-5 space-y-3">
          <CampaignsPanel />
        </TabsContent>

        <TabsContent value="reviews" className="mt-5 space-y-3">
          {reviews.map((r) => (
            <div key={r.review_id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`admin-review-${r.review_id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-gray-900">{r.name}</div>
                  <div className="text-sm text-gray-500">{r.area}</div>
                  <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`w-4 h-4 ${i <= r.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-gray-300"}`} />)}</div>
                </div>
                <Button size="sm" variant={r.approved ? "outline" : "default"} className="rounded-full" onClick={() => toggleReview(r.review_id, !r.approved)}>
                  {r.approved ? "Hide" : "Approve"}
                </Button>
              </div>
              <p className="mt-2 text-sm text-gray-800">"{r.comment}"</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="signups" className="mt-5">
          <AbandonedSignups />
        </TabsContent>

        <TabsContent value="leads" className="mt-5">
          <LeadsPanel />
        </TabsContent>

        <TabsContent value="settings" className="mt-5 space-y-4">
          <ThumbtackWebhookPanel />
          <div className="bg-white rounded-2xl border border-gray-200 p-6 max-w-2xl">
            <div className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-[#047857]" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Review automation</div>
            </div>
            <h2 className="mt-1 text-xl font-extrabold font-[Outfit] tracking-tighter text-gray-900">Google Review request</h2>
            <p className="mt-1 text-sm text-gray-600">After a job is marked complete, customers get a thank-you + tip prompt immediately. They also get a Google review request automatically after the delay below.</p>

            <div className="mt-5 space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">Google Review URL</Label>
                <div className="mt-1 flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    placeholder="https://g.page/r/your-business-id/review"
                    value={settings.google_review_url}
                    onChange={(e) => setSettings({ ...settings, google_review_url: e.target.value })}
                    data-testid="settings-google-review-url"
                  />
                </div>
                <p className="mt-1 text-[11px] text-gray-500">Paste your Google Business "Get more reviews" short link. If empty, review requests are skipped.</p>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">Delay before sending (hours)</Label>
                <Input
                  type="number"
                  min={1}
                  max={168}
                  value={settings.review_delay_hours}
                  onChange={(e) => setSettings({ ...settings, review_delay_hours: parseInt(e.target.value) || 24 })}
                  data-testid="settings-review-delay"
                />
                <p className="mt-1 text-[11px] text-gray-500">Recommended: 24 hours. Gives customers time to inspect the work.</p>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">Quote payment policy</Label>
                <label className="mt-2 flex items-start gap-3 rounded-2xl border border-gray-200 px-4 py-3 cursor-pointer hover:bg-emerald-50/30 transition" data-testid="settings-require-payment-row">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-[#047857]"
                    checked={!!settings.require_payment_before_scheduling}
                    onChange={(e) => setSettings({ ...settings, require_payment_before_scheduling: e.target.checked })}
                    data-testid="settings-require-payment"
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px] font-extrabold text-gray-900">Require payment before scheduling</span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">When ON, customers cannot accept a quote without paying deposit or full. When OFF, customers can accept now and pay later (Pay Now button stays available).</span>
                  </span>
                </label>
              </div>
              <Button
                onClick={saveSettings}
                disabled={savingSettings}
                className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-bold"
                data-testid="settings-save"
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save settings"}
              </Button>

              {/* Customer-site preview (admin only) */}
              <div className="pt-4 border-t border-gray-100">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">Preview</Label>
                <p className="mt-1 text-[12px] text-gray-600">See exactly what customers see — without losing your admin session.</p>
                <Button
                  variant="outline"
                  className="mt-2 rounded-full font-bold"
                  data-testid="open-customer-view-btn"
                  onClick={() => {
                    sessionStorage.setItem("jdsn_customer_view_override", "1");
                    navigate("/");
                  }}
                >
                  <Eye className="w-4 h-4 mr-1.5" /> Open customer view
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ===== Edit Customer Dialog ===== */}
      <EditCustomerDialog
        open={!!editCust}
        onClose={() => setEditCust(null)}
        customer={editCust}
        customers={customers}
        services={services}
        onSaved={() => loadAll()}
        onOpenCustomer={(target) => {
          // Prefer the freshest row from state (fuller shape); fall back to
          // the 409 payload so we always open something useful.
          const full = customers.find((c) => c.user_id === target.user_id);
          setEditCust(full || target);
        }}
      />
      <ChangePaymentMethodDialog
        open={!!pmDialogCust}
        onOpenChange={(v) => !v && setPmDialogCust(null)}
        customer={pmDialogCust}
        onChanged={() => loadAll()}
      />
      <UpdatePriceDialog
        open={!!pricingCustomer}
        onOpenChange={(v) => !v && setPricingCustomer(null)}
        customer={pricingCustomer}
        allowScopeToggle={false}
        onSaved={() => loadAll()}
      />
      <PropertyMapDialog
        open={!!mapBooking}
        onOpenChange={(o) => !o && setMapBooking(null)}
        booking={mapBooking}
      />

      {/* ===== Add Customer Dialog ===== */}
      <Dialog open={addCustOpen} onOpenChange={setAddCustOpen}>
        <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="add-customer-dialog">
          <DialogHeader>
            <DialogTitle>Add new customer</DialogTitle>
            <DialogDescription>
              Create a CRM customer record. If an email is provided we'll automatically email them an account activation link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* ---- Contact info ---- */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Name <span className="text-rose-500">*</span></Label>
                <Input value={newCust.name} onChange={(e) => setNewCust({ ...newCust, name: e.target.value })} data-testid="new-cust-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} data-testid="new-cust-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} data-testid="new-cust-phone" />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={newCust.address}
                  onChange={(e) => setNewCust({ ...newCust, address: e.target.value })}
                  onBlur={async (e) => {
                    // iter139 — Auto-lookup property size on blur so the
                    // admin never has to type sqft.
                    const addr = (e.target.value || "").trim();
                    if (!addr || newCust._auto_lookup_in_flight) return;
                    setNewCust((c) => ({ ...c, _auto_lookup_in_flight: true, _auto_lookup_error: null }));
                    try {
                      const { data } = await api.post("/admin/property/auto-lookup", { address: addr });
                      if (data?.ok) {
                        setNewCust((c) => ({
                          ...c,
                          square_feet: data.square_feet,
                          acres: data.acres,
                          lat: data.lat,
                          lng: data.lng,
                          formatted_address: data.formatted_address || addr,
                          _auto_lookup_source: data.source,
                          _auto_lookup_in_flight: false,
                          _auto_lookup_error: null,
                        }));
                      } else {
                        setNewCust((c) => ({
                          ...c,
                          _auto_lookup_in_flight: false,
                          _auto_lookup_error: data?.message || "Unable to calculate property size automatically. Please verify the address or enter the size manually.",
                        }));
                      }
                    } catch (err) {
                      setNewCust((c) => ({
                        ...c,
                        _auto_lookup_in_flight: false,
                        _auto_lookup_error: "Unable to calculate property size automatically. Please verify the address or enter the size manually.",
                      }));
                    }
                  }}
                  data-testid="new-cust-address"
                />
                {newCust._auto_lookup_in_flight && (
                  <div className="mt-1 text-[11px] text-emerald-700 font-semibold" data-testid="new-cust-auto-lookup-loading">
                    Verifying address and calculating property size…
                  </div>
                )}
                {newCust.square_feet > 0 && !newCust._auto_lookup_in_flight && !newCust._auto_lookup_error && (
                  <div className="mt-1 text-[11px] text-emerald-700 font-semibold flex items-center gap-2" data-testid="new-cust-auto-lookup-result">
                    <span>✓ Property Verified · {Number(newCust.square_feet).toLocaleString()} sq ft{newCust.acres ? ` · ${newCust.acres} ac` : ""}</span>
                  </div>
                )}
                {newCust._auto_lookup_error && (
                  <div className="mt-1 text-[11px] text-amber-700 font-semibold" data-testid="new-cust-auto-lookup-error">
                    {newCust._auto_lookup_error}
                  </div>
                )}
              </div>
              <div>
                <Label>Square Feet {newCust.square_feet > 0 && !newCust._auto_lookup_error ? <span className="text-[10px] text-emerald-700 font-bold">(auto-detected · admin can override)</span> : null}</Label>
                <Input
                  type="number"
                  min="0"
                  value={newCust.square_feet || ""}
                  onChange={(e) => setNewCust({ ...newCust, square_feet: e.target.value ? Number(e.target.value) : null })}
                  data-testid="new-cust-square-feet"
                  placeholder="Auto-populated after address"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={newCust.notes} onChange={(e) => setNewCust({ ...newCust, notes: e.target.value })} rows={2} data-testid="new-cust-notes" />
            </div>

            {/* ---- Recurring schedule ---- */}
            <div className="rounded-2xl bg-emerald-50/60 border border-emerald-200 p-3.5">
              <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-emerald-800">Service schedule</div>
              <div className="mt-2.5">
                <RecurringFrequencyFields
                  value={newCust}
                  onChange={(v) => setNewCust({ ...newCust, ...v })}
                  services={services}
                />
              </div>
            </div>

            {/* ---- Invitation toggle ---- */}
            <label className="flex items-start gap-3 rounded-2xl border border-gray-200 px-3 py-2.5 cursor-pointer" data-testid="auto-invite-toggle">
              <input
                type="checkbox"
                checked={newCust.auto_send_invitation}
                onChange={(e) => setNewCust({ ...newCust, auto_send_invitation: e.target.checked })}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="text-sm font-extrabold text-gray-900">Send activation invitation now</div>
                <div className="text-[11px] text-gray-500">Customer gets an email with a "Create My Account" link (14-day expiry).</div>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCustOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={submitNewCustomer} className="rounded-full bg-[#047857] hover:bg-[#065F46]" data-testid="add-customer-submit">Save customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Restore Archived / Deleted Customer Prompt ===== */}
      <Dialog open={!!restorePrompt} onOpenChange={(v) => !v && setRestorePrompt(null)}>
        <DialogContent className="sm:max-w-md" data-testid="restore-archived-dialog">
          <DialogHeader>
            <DialogTitle>
              {restorePrompt?.__kind === "deleted"
                ? "This email belongs to a deleted customer"
                : "Archived customer found"}
            </DialogTitle>
            <DialogDescription>
              {restorePrompt?.__kind === "deleted"
                ? "We found a customer in the Trash that matches this contact info. Restore them, delete them forever to release the email/phone, or create a new customer anyway."
                : "We found an archived customer that matches this contact info. You can restore them or create a brand-new customer with different details."}
            </DialogDescription>
          </DialogHeader>
          {restorePrompt && (
            <div className={`rounded-2xl border p-4 space-y-1 ${restorePrompt.__kind === "deleted" ? "border-rose-200 bg-rose-50/60" : "border-amber-200 bg-amber-50/60"}`}>
              <div className="text-[13px] font-extrabold text-gray-900" data-testid="restore-name">
                {restorePrompt.name || (restorePrompt.__kind === "deleted" ? "Deleted customer" : "Archived customer")}
              </div>
              {restorePrompt.email && <div className="text-[12px] text-gray-700">📧 {restorePrompt.email}</div>}
              {restorePrompt.phone && <div className="text-[12px] text-gray-700">📞 {restorePrompt.phone}</div>}
              {restorePrompt.address && <div className="text-[12px] text-gray-700">📍 {restorePrompt.address}</div>}
              {restorePrompt.archived_at && (
                <div className="text-[11px] text-amber-700 mt-1">
                  Archived on {new Date(restorePrompt.archived_at).toLocaleDateString()}
                </div>
              )}
              {restorePrompt.deleted_at && (
                <div className="text-[11px] text-rose-700 mt-1">
                  🗑 Deleted on {new Date(restorePrompt.deleted_at).toLocaleString()}
                  {restorePrompt.deleted_by && <> · by {restorePrompt.deleted_by}</>}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-2 gap-1.5">
            <Button variant="outline" onClick={() => setRestorePrompt(null)} className="rounded-full" data-testid="restore-cancel-btn">
              Cancel
            </Button>
            {restorePrompt?.__kind === "deleted" && (
              <Button
                variant="outline"
                onClick={deleteForeverFromRestorePrompt}
                className="rounded-full border-rose-400 text-rose-700 hover:bg-rose-50 font-extrabold"
                data-testid="delete-forever-from-prompt-btn"
              >
                🗑 Delete Forever
              </Button>
            )}
            <Button variant="outline" onClick={createNewDespiteArchived} className="rounded-full border-gray-300" data-testid="create-new-anyway-btn">
              Create New Anyway
            </Button>
            <Button onClick={restoreArchivedCustomer} className="rounded-full bg-[#047857] hover:bg-[#065F46] font-extrabold" data-testid="restore-existing-btn">
              ♻ Restore Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Schedule for Customer Dialog ===== */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="sm:max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule service for {schedCustomer?.name || "customer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Service</Label>
              <Select value={schedForm.service_key} onValueChange={(v) => setSchedForm({ ...schedForm, service_key: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{services.map(s => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={schedForm.preferred_date} onChange={(e) => setSchedForm({ ...schedForm, preferred_date: e.target.value })} data-testid="sched-date" />
              </div>
              <div>
                <Label>Time</Label>
                <Input value={schedForm.preferred_time} onChange={(e) => setSchedForm({ ...schedForm, preferred_time: e.target.value })} data-testid="sched-time" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={schedForm.frequency} onValueChange={(v) => setSchedForm({ ...schedForm, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ_OPTIONS.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sqft</Label>
                <Input type="number" value={schedForm.square_feet} onChange={(e) => setSchedForm({ ...schedForm, square_feet: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div>
              <Label>Payment method</Label>
              <Select value={schedForm.payment_method} onValueChange={(v) => setSchedForm({ ...schedForm, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_OPTIONS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={schedForm.notes} onChange={(e) => setSchedForm({ ...schedForm, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedOpen(false)} disabled={schedSubmitting} className="rounded-full">Cancel</Button>
            <Button onClick={submitSchedule} disabled={schedSubmitting} className="rounded-full bg-[#047857] hover:bg-[#065F46]" data-testid="sched-submit">
              {schedSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Cancel Job Dialog ===== */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-md" data-testid="cancel-job-dialog">
          <DialogHeader>
            <DialogTitle>Cancel job for {cancelBooking?.name}?</DialogTitle>
            <DialogDescription>
              The customer will be notified by SMS and email with the reason below.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label>Reason <span className="text-rose-500">*</span></Label>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. weather conditions, equipment issue, schedule conflict…"
              data-testid="cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} className="rounded-full">Keep job</Button>
            <Button onClick={submitCancel} disabled={cancelling} className="rounded-full bg-rose-600 hover:bg-rose-700 text-white" data-testid="cancel-submit">
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel job + notify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Offer Counter / Accept / Reject Dialog ===== */}
      <Dialog open={counterOpen} onOpenChange={setCounterOpen}>
        <DialogContent className="sm:max-w-md" data-testid="counter-offer-dialog">
          <DialogHeader>
            <DialogTitle>
              {counterAction === "accept" && `Accept ${counterOffer?.customer_name}'s offer`}
              {counterAction === "counter" && `Counter ${counterOffer?.customer_name}`}
              {counterAction === "reject" && `Reject ${counterOffer?.customer_name}'s offer`}
            </DialogTitle>
            <DialogDescription>
              {counterAction === "accept" && counterOffer?.preferred_date && "A confirmed booking will be auto-created for the customer's preferred date."}
              {counterAction === "counter" && "Propose a different price. Customer will be notified."}
              {counterAction === "reject" && "Customer will be notified that the offer wasn't accepted."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            {counterOffer && (
              <div className="bg-gray-50 rounded-xl p-3 text-sm">
                Estimate: <strong>${(counterOffer.estimated_price || 0).toFixed(2)}</strong> · They offered: <strong className="text-[#047857]">${(counterOffer.offered_price || 0).toFixed(2)}</strong>
              </div>
            )}
            {counterAction === "counter" && (
              <div>
                <Label>Counter price</Label>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-2xl font-extrabold text-gray-400">$</span>
                  <Input type="number" min="1" step="0.01" value={counterPrice} onChange={(e) => setCounterPrice(e.target.value)} className="text-2xl font-extrabold h-12 tabular-nums" data-testid="admin-counter-price" />
                </div>
              </div>
            )}
            <div>
              <Label>Message to customer (optional)</Label>
              <Textarea rows={2} value={counterMessage} onChange={(e) => setCounterMessage(e.target.value)} placeholder="e.g. 'We can do this if we mow your front yard too.'" data-testid="admin-counter-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCounterOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={submitCounter}
              className={`rounded-full font-bold ${counterAction === "reject" ? "bg-rose-600 hover:bg-rose-700" : "bg-[#047857] hover:bg-[#065F46]"} text-white`}
              data-testid="admin-counter-submit"
            >
              {counterAction === "accept" && `Accept $${(counterOffer?.offered_price || 0).toFixed(2)}`}
              {counterAction === "counter" && `Send counter $${parseFloat(counterPrice || 0).toFixed(2)}`}
              {counterAction === "reject" && "Reject offer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Send Quote Dialog ===== */}
      <SendQuoteDialog
        open={quoteDialogOpen}
        onOpenChange={setQuoteDialogOpen}
        customer={quoteCustomer}
        onSent={loadAll}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, "data-testid": testId }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <Icon className="w-5 h-5 text-[#047857]" />
        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold">{label}</div>
      </div>
      <div className="mt-2 text-2xl font-extrabold tracking-tight text-gray-900" data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5" data-testid={testId ? `${testId}-sub` : undefined}>{sub}</div>}
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-500">
      <ListChecks className="w-8 h-8 mx-auto text-gray-300" />
      <p className="mt-2">{label}</p>
    </div>
  );
}

function ConvCard({ label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-3 py-3 sm:px-4 sm:py-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold">{label}</div>
      <div className="mt-1 text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5 uppercase tracking-wider font-bold">{sub}</div>}
    </div>
  );
}
